import React, { useState } from 'react';

const QuickAdd = ({ onProgramAdded, isVisible, onClose }) => {
  const [inputValue, setInputValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [step, setStep] = useState('input'); // 'input', 'extracted', 'confirm'

  const isUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch {
      return false;
    }
  };

  const parseUrlForProgram = async (url) => {
    // Mock URL parsing - in real implementation, this would call a service
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Mock extracted data based on common program URL patterns
    const mockData = {
      name: 'Research Science Institute (RSI)',
      organization: 'MIT',
      url: url,
      description: 'A six-week summer research program for high school students to conduct original research with MIT faculty.',
      category: 'research',
      duration: '6 weeks',
      applicationDeadline: '2025-02-01',
      cost: 'free',
      competitiveness: 'ultra'
    };

    return mockData;
  };

  const categorizeByName = (programName) => {
    const name = programName.toLowerCase();
    
    if (name.includes('research') || name.includes('science') || name.includes('rsi')) {
      return 'research';
    } else if (name.includes('medical') || name.includes('health') || name.includes('hospital')) {
      return 'medical';
    } else if (name.includes('veterinary') || name.includes('animal')) {
      return 'veterinary';
    } else if (name.includes('ai') || name.includes('computer') || name.includes('tech')) {
      return 'technology';
    } else if (name.includes('justice') || name.includes('policy') || name.includes('advocacy')) {
      return 'socialJustice';
    }
    
    return 'other';
  };

  const assessCompetitiveness = (programName, organization) => {
    const name = programName.toLowerCase();
    const org = organization.toLowerCase();
    
    // Ultra competitive programs
    if (name.includes('rsi') || name.includes('tasp') || 
        (name.includes('research') && (org.includes('mit') || org.includes('harvard')))) {
      return 'ultra';
    }
    
    // High competitiveness - governor's schools, ivy programs
    if (name.includes('governor') || org.includes('duke') || org.includes('stanford')) {
      return 'high';
    }
    
    // Default to moderate
    return 'moderate';
  };

  const handleQuickAdd = async () => {
    if (!inputValue.trim()) return;
    
    setIsProcessing(true);
    
    try {
      if (isUrl(inputValue)) {
        // URL parsing flow
        const extracted = await parseUrlForProgram(inputValue);
        setExtractedData(extracted);
        setStep('extracted');
      } else {
        // Manual entry flow
        const programData = {
          name: inputValue,
          organization: '',
          category: categorizeByName(inputValue),
          competitiveness: 'moderate',
          status: 'discovered',
          notes: 'Added via Quick Add',
          researchStatus: 'discovered'
        };
        setExtractedData(programData);
        setStep('extracted');
      }
    } catch (error) {
      console.error('Error processing program:', error);
      // Handle error - show user-friendly message
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmAdd = () => {
    if (extractedData && onProgramAdded) {
      onProgramAdded(extractedData);
      resetForm();
      onClose();
    }
  };

  const resetForm = () => {
    setInputValue('');
    setExtractedData(null);
    setStep('input');
    setIsProcessing(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-stone-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Quick Add Program</h2>
              <p className="text-stone-600 text-sm mt-1">
                Add a program by URL or name - we'll help fill in the details
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-stone-400 hover:text-stone-600 text-xl"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6">
          {step === 'input' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Program URL or Name
                </label>
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Paste program URL or type program name..."
                  className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  autoFocus
                />
                <div className="mt-2 text-xs text-stone-500">
                  💡 Tip: URLs work best for automatic data extraction
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleQuickAdd}
                  disabled={!inputValue.trim() || isProcessing}
                  className="flex-1 px-4 py-2 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {isUrl(inputValue) ? 'Extracting data...' : 'Processing...'}
                    </div>
                  ) : (
                    'Add Program'
                  )}
                </button>
                <button
                  onClick={handleClose}
                  className="px-4 py-2 border border-stone-300 text-stone-700 font-medium rounded-lg hover:bg-stone-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {step === 'extracted' && extractedData && (
            <div className="space-y-6">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="text-emerald-600">✓</div>
                  <h3 className="font-medium text-emerald-800">Program Details Extracted</h3>
                </div>
                <p className="text-emerald-700 text-sm">
                  We've automatically filled in the details below. Review and edit as needed.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Program Name</label>
                  <input
                    type="text"
                    value={extractedData.name}
                    onChange={(e) => setExtractedData({...extractedData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Organization</label>
                  <input
                    type="text"
                    value={extractedData.organization}
                    onChange={(e) => setExtractedData({...extractedData, organization: e.target.value})}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                  <select
                    value={extractedData.category}
                    onChange={(e) => setExtractedData({...extractedData, category: e.target.value})}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="research">Research</option>
                    <option value="medical">Medical & Healthcare</option>
                    <option value="veterinary">Veterinary & Animal Science</option>
                    <option value="technology">Technology & AI</option>
                    <option value="socialJustice">Social Justice & Policy</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Competitiveness</label>
                  <select
                    value={extractedData.competitiveness}
                    onChange={(e) => setExtractedData({...extractedData, competitiveness: e.target.value})}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="low">Safety</option>
                    <option value="moderate">Target</option>
                    <option value="high">Reach</option>
                    <option value="ultra">Ultra Reach</option>
                  </select>
                </div>

                {extractedData.duration && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Duration</label>
                    <input
                      type="text"
                      value={extractedData.duration}
                      onChange={(e) => setExtractedData({...extractedData, duration: e.target.value})}
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                  </div>
                )}

                {extractedData.applicationDeadline && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Application Deadline</label>
                    <input
                      type="date"
                      value={extractedData.applicationDeadline}
                      onChange={(e) => setExtractedData({...extractedData, applicationDeadline: e.target.value})}
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                  </div>
                )}
              </div>

              {extractedData.description && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea
                    value={extractedData.description}
                    onChange={(e) => setExtractedData({...extractedData, description: e.target.value})}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    rows="3"
                  />
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={handleConfirmAdd}
                  className="flex-1 px-4 py-2 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700"
                >
                  Add to Collection
                </button>
                <button
                  onClick={() => setStep('input')}
                  className="px-4 py-2 border border-stone-300 text-stone-700 font-medium rounded-lg hover:bg-stone-50"
                >
                  Back
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickAdd;